/**
 * Batched fleet renderer for stress test — renders N fleets in one draw per ship type.
 * Optimizations: frustum culling, distance LOD, shared shader, no separation.
 */
import type { Renderable } from '../renderer/RenderPipeline';
import type { Camera } from '../renderer/Camera';
import { InstancedShipRenderer, createSharedShipShader } from './InstancedShipRenderer';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';
import { FleetFormation, type TypeSlice } from '../systems/FleetFormation';
import type { FleetState } from '../systems/FleetState';
import type { ShaderProgram } from '../renderer/ShaderProgram';
import { loadGlb } from '../renderer/GlbParser';

const FLOATS_PER_MAT = 16;
const MAX_FLEETS = 100;
const MAX_SHIPS_PER_TYPE = MAX_FLEETS * 12;

/** Approximate world-space radius of a fleet formation for frustum culling. */
const FLEET_RADIUS = 2.5;

/** Distance thresholds for LOD (squared, to avoid sqrt). */
const LOD_FULL_DIST2 = 40 * 40;       // < 40 units: full detail
const LOD_HALF_DIST2 = 100 * 100;     // < 100 units: half ships
// > 100 units: quarter ships

export interface FleetPosition {
  state: FleetState;
  x: number;
  y: number;
  z: number;
  dirX: number;
  dirZ: number;
  turnRate: number;
}

export class StressFleetRenderer implements Renderable {
  private _sharedShader: ShaderProgram;
  private _renderers: InstancedShipRenderer[] = [];
  private _formations: FleetFormation[] = [];
  private _mergeBuf = new Float32Array(MAX_SHIPS_PER_TYPE * FLOATS_PER_MAT);
  private _time = 0;
  private _lodComp = new Array<number>(SHIP_TYPE_COUNT);

  /** Number of fleets actually rendered last frame (after culling). */
  visibleFleets = 0;

  constructor(
    gl: WebGL2RenderingContext,
    spacing: number,
    maxRadius: number,
  ) {
    this._sharedShader = createSharedShipShader(gl);
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      this._renderers.push(new InstancedShipRenderer(gl, SHIP_TYPE_DEFS[i], MAX_SHIPS_PER_TYPE, this._sharedShader));
    }
    for (let i = 0; i < MAX_FLEETS; i++) {
      this._formations.push(new FleetFormation(spacing, maxRadius));
    }
  }

  async loadModels(): Promise<void> {
    const loaded = new Map<string, ReturnType<typeof loadGlb>>();
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const url = SHIP_TYPE_DEFS[i].modelUrl;
      if (!loaded.has(url)) loaded.set(url, loadGlb(url));
    }
    const meshes = new Map<string, Awaited<ReturnType<typeof loadGlb>>>();
    for (const [url, promise] of loaded) {
      meshes.set(url, await promise);
    }
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const mesh = meshes.get(SHIP_TYPE_DEFS[i].modelUrl);
      if (mesh) await this._renderers[i].loadMesh(mesh);
    }
  }

  update(dt: number): void { this._time += dt; }

  updateFormation(positions: readonly FleetPosition[], camera: Camera, count?: number): void {
    const n = Math.min(count ?? positions.length, MAX_FLEETS);
    const camX = camera.position.x, camY = camera.position.y, camZ = camera.position.z;

    let visible = 0;
    for (let fi = 0; fi < n; fi++) {
      const pos = positions[fi];

      // Frustum culling
      if (!camera.sphereInFrustum(pos.x, pos.y, pos.z, FLEET_RADIUS)) continue;

      // Distance LOD — reduce composition for far fleets
      const dx = pos.x - camX, dy = pos.y - camY, dz = pos.z - camZ;
      const dist2 = dx * dx + dy * dy + dz * dz;
      let lodScale: number;
      if (dist2 < LOD_FULL_DIST2) lodScale = 1.0;
      else if (dist2 < LOD_HALF_DIST2) lodScale = 0.5;
      else lodScale = 0.25;

      const comp = pos.state.composition;
      const lodComp = this._lodComp;
      for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
        lodComp[i] = Math.max(1, Math.round(comp[i] * lodScale));
      }

      const form = this._formations[visible];
      form.compute(
        lodComp, pos.x, pos.y, pos.z,
        pos.dirX, pos.dirZ, pos.turnRate,
        this._time, false, true, // skipSeparation = true
      );
      visible++;
    }
    this.visibleFleets = visible;

    // Merge per-type instance data
    for (let ti = 0; ti < SHIP_TYPE_COUNT; ti++) {
      let offset = 0;
      for (let fi = 0; fi < visible; fi++) {
        const s: TypeSlice = this._formations[fi].slices[ti];
        const len = s.count * FLOATS_PER_MAT;
        for (let j = 0; j < len; j++) {
          this._mergeBuf[offset + j] = s.data[j];
        }
        offset += len;
      }
      this._renderers[ti].updateInstances(this._mergeBuf, offset / FLOATS_PER_MAT);
    }
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    for (let i = 0; i < this._renderers.length; i++) {
      this._renderers[i].render(gl, camera, this._time);
    }
  }

  dispose(): void {
    for (let i = 0; i < this._renderers.length; i++) {
      this._renderers[i].dispose();
    }
    this._sharedShader.dispose();
  }
}
